// sheet — the contact-Sheet PNG generator (framework-agnostic).
//
// A port of the device-served `web/sheet.js` contact sheet. It lays a clip's
// frames out on a grid and renders one shareable PNG. Two pieces, kept separate so
// the load-bearing logic is unit-testable without a canvas/DOM:
//
//   • sampleFrameIndices() — the PURE sampling math. Picks which decoded frame
//     goes in each grid slot. This is the "whole-clip even sampling" fix that
//     shipped in web/sheet.js (first…last inclusive), preserved here exactly.
//   • renderContactSheet() — draws the sampled frames onto a canvas and encodes a
//     PNG Blob. Needs a DOM/canvas; thin, untested wrapper over the pure parts.
//
// The decoder (gifDecode) is a separate core; the Sheet ignores frame delays.

import type { GifFrame } from "./gifDecode";

/** A layout: the grid shape + the per-image cap, matching web/sheet.js. */
export interface SheetLayout {
  /** Number of columns. */
  readonly cols: number;
  /** Maximum frames sampled onto the grid (= cols*rows at full fill). */
  readonly maxFrames: number;
}

/** The two layouts web/sheet.js offers: a 5×5 square and a 4×8 strip. */
export const SHEET_LAYOUTS = {
  square: { cols: 5, maxFrames: 25 },
  strip: { cols: 4, maxFrames: 32 },
} as const satisfies Record<string, SheetLayout>;

export type SheetLayoutName = keyof typeof SHEET_LAYOUTS;

/**
 * Pick the source-frame index for every grid slot — the whole-clip even sampling.
 *
 * Ported verbatim from web/sheet.js (the recently-shipped fix). Given a clip of
 * `length` frames and a layout, we sample `n = min(length, maxFrames)` frames
 * **evenly across the whole clip** — first…last **inclusive** — rather than the
 * first `n`. A clip with more frames than cells then shows beginning→end at a lower
 * frame rate, instead of clipping the tail.
 *
 *   span = (length - 1) / (n - 1)   maps slot 0 → frame 0 and slot n-1 → frame
 *                                    (length-1), the LAST frame.
 *   slot i → round((i % n) * span)
 *
 * The grid has `cols*rows` cells (rows = ceil(n / cols)); the last partial row is
 * filled by looping the sampled set (`i % n`), exactly like web/sheet.js.
 *
 * When `length <= maxFrames` the clip fits: `n === length`, so `span === 1` and
 * each slot maps to itself — short clips are unchanged (identity), still looping to
 * fill the last partial row.
 *
 * @returns one source-frame index per grid slot, length `cols * rows`.
 */
export function sampleFrameIndices(
  length: number,
  layout: SheetLayout,
): number[] {
  const { cols, maxFrames } = layout;
  const n = Math.min(length, maxFrames);
  const rows = Math.max(1, Math.ceil(n / cols));
  const total = cols * rows;
  // span === 1 when the clip fits (n === length), so the mapping is the identity.
  const span = n > 1 ? (length - 1) / (n - 1) : 0;
  const out: number[] = new Array(total);
  for (let i = 0; i < total; i++) {
    out[i] = Math.round((i % n) * span);
  }
  return out;
}

/** Grid geometry for a sampled clip of `length` frames under `layout`. */
export interface SheetGrid {
  readonly cols: number;
  readonly rows: number;
  /** The source-frame index for each of `cols*rows` slots (row-major). */
  readonly slots: number[];
}

/** Compute the full grid (cols/rows + the per-slot source indices) for a clip. */
export function planSheet(length: number, layout: SheetLayout): SheetGrid {
  const slots = sampleFrameIndices(length, layout);
  const rows = slots.length / layout.cols;
  return { cols: layout.cols, rows, slots };
}

/** Options for rendering the contact-sheet PNG. */
export interface RenderSheetOptions {
  /** Layout name (default "square"). */
  readonly layout?: SheetLayoutName;
  /**
   * "seamless" tiles edge-to-edge (default); "hairline" inserts a thin gap
   * between cells — both as in web/sheet.js.
   */
  readonly look?: "seamless" | "hairline";
  /** Target longest-edge size in px (web/sheet.js uses 1080). */
  readonly size?: number;
  /** Background fill behind/between cells (web/sheet.js uses #0d0d0d). */
  readonly background?: string;
}

/**
 * Render a clip's decoded frames into a contact-sheet PNG Blob.
 *
 * Wraps the pure sampling (sampleFrameIndices) with the canvas work web/sheet.js
 * does: build a per-frame bitmap, lay the sampled frames on the grid, encode a PNG.
 * Requires a DOM (a real browser, or jsdom with a canvas backend) — the pure parts
 * above are what the unit tests exercise.
 *
 * @returns the PNG as a Blob.
 */
export async function renderContactSheet(
  frames: readonly GifFrame[],
  options: RenderSheetOptions = {},
): Promise<Blob> {
  if (frames.length === 0) throw new Error("no frames to render");

  const layout = SHEET_LAYOUTS[options.layout ?? "square"];
  const look = options.look ?? "seamless";
  const size = options.size ?? 1080;
  const background = options.background ?? "#0d0d0d";

  const { cols, rows, slots } = planSheet(frames.length, layout);
  const cell = Math.floor(size / Math.max(cols, rows));
  const gap = look === "hairline" ? Math.max(2, Math.round(cell * 0.03)) : 0;

  const canvas = document.createElement("canvas");
  canvas.width = cols * cell + (cols - 1) * gap;
  canvas.height = rows * cell + (rows - 1) * gap;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");
  ctx.imageSmoothingEnabled = true;
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // One bitmap per decoded frame, reused across the (possibly looping) slots.
  const bitmaps = frames.map(frameToBitmap);
  for (let i = 0; i < slots.length; i++) {
    const bmp = bitmaps[slots[i]!]!;
    const dx = (i % cols) * (cell + gap);
    const dy = Math.floor(i / cols) * (cell + gap);
    ctx.drawImage(bmp, dx, dy, cell, cell);
  }

  return canvasToPngBlob(canvas);
}

/** Default download filename for a sheet, mirroring web/sheet.js. */
export function sheetFilename(
  clipFilename: string,
  layout: SheetLayoutName = "square",
  look: RenderSheetOptions["look"] = "seamless",
): string {
  const base = clipFilename.replace(/\.gif$/i, "");
  const shape = layout === "square" ? "5x5" : "4x8";
  return `${base}-${shape}-${look}.png`;
}

// --- canvas helpers (DOM-only; not unit-tested) ----------------------------

/** Paint one decoded GIF frame (palette + indices) into an offscreen canvas. */
function frameToBitmap(frame: GifFrame): CanvasImageSource {
  const canvas = document.createElement("canvas");
  canvas.width = frame.width;
  canvas.height = frame.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");
  const img = ctx.createImageData(frame.width, frame.height);
  const { palette, indices } = frame;
  const data = img.data;
  for (let k = 0; k < indices.length; k++) {
    const c = indices[k]! * 3;
    const o = k * 4;
    data[o] = palette[c]!;
    data[o + 1] = palette[c + 1]!;
    data[o + 2] = palette[c + 2]!;
    data[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/** Encode a canvas to a PNG Blob (Promise wrapper over canvas.toBlob). */
function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("PNG encoding failed"));
    }, "image/png");
  });
}
